import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getPeriodClosingAuditLogs } from '../services/api';

interface AuditLog {
  id: string;
  action: 'CREATED' | 'STARTED' | 'COMPLETED' | 'FAILED' | 'REOPENED' | 'NOTES_UPDATED' | 'STATUS_CHANGED';
  description: string;
  previousData?: any;
  newData?: any;
  performedBy: string;
  performedAt: string;
}

interface AuditLogState {
  auditLogs: { [periodClosingId: string]: AuditLog[] };
  loading: { [periodClosingId: string]: boolean };
  error: string | null;
  lastFetch: { [periodClosingId: string]: number };

  fetchAuditLogs: (periodClosingId: string, forceRefresh?: boolean) => Promise<void>;
  clearError: () => void;
}


const useAuditLogStore = create<AuditLogState>()(
  persist(
    (set, get) => ({
      auditLogs: {},
      loading: {},
      error: null,
      lastFetch: {},

      fetchAuditLogs: async (periodClosingId: string, forceRefresh = false) => {
        const { lastFetch, loading, auditLogs } = get();
        const now = Date.now();

        // 如果正在載入，直接返回
        if (loading[periodClosingId]) {
          return;
        }

        // 檢查快取是否有效（除非強制刷新）
        const lastFetchTime = lastFetch[periodClosingId];
        if (!forceRefresh && lastFetchTime && auditLogs[periodClosingId]) {
          return;
        }

        // 設定載入狀態
        set(state => ({
          loading: { ...state.loading, [periodClosingId]: true },
          error: null,
        }));

        try {
          const response = await getPeriodClosingAuditLogs(periodClosingId);

          set(state => ({
            auditLogs: { ...state.auditLogs, [periodClosingId]: response?.data || [] },
            lastFetch: { ...state.lastFetch, [periodClosingId]: now },
            loading: { ...state.loading, [periodClosingId]: false },
            error: null,
          }));
        } catch (error: any) {
          const errorMessage = error.response?.data?.error || '載入歷史記錄失敗';
          console.error('載入審計日誌失敗:', error);

          set(state => ({
            loading: { ...state.loading, [periodClosingId]: false },
            error: errorMessage,
          }));
        }
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'audit-logs-storage',
      partialize: (state) => ({
        auditLogs: state.auditLogs,
        lastFetch: state.lastFetch,
      }),
      merge: (persistedState: any, currentState: AuditLogState) => ({
        ...currentState,
        ...persistedState,
        auditLogs: persistedState?.auditLogs || {},
        lastFetch: persistedState?.lastFetch || {},
        loading: {}, // 重新整理時不顯示載入狀態
        error: null, // 重置錯誤狀態
      }),
    }
  )
);

export default useAuditLogStore;